import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import type { Editor } from "@tiptap/react";
import { useQuery } from "convex/react";
import { ArrowLeft, AtSign, ChevronRight, FileUp, Plus } from "lucide-react";
import { type ChangeEvent, type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";
import { AsyncBoundary } from "@/components/ui/async-boundary";
import { buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface AttachMenuProps {
  editor: Editor | null;
  projectId?: Id<"projects"> | null;
  disabled?: boolean;
  accept?: string;
  onFilesSelected?: (files: File[]) => void | Promise<void>;
}

type MenuView = "root" | "types" | "entities";

interface SelectedType {
  key: string;
  label: string;
}

/** The menu's own placeholder line — what it says while loading, and when a list is empty. */
function MenuNote({ children }: { children: ReactNode }) {
  return <p className="px-2 py-3 text-center text-xs text-muted-foreground">{children}</p>;
}

function MenuRow({
  icon,
  title,
  subtitle,
  trailing,
  disabled,
  onClick,
}: {
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  trailing?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2.5 px-2 py-1.5 text-left text-xs",
        "hover:bg-accent focus-visible:bg-accent focus-visible:outline-none",
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
      )}
    >
      {icon ? <span className="shrink-0 text-muted-foreground">{icon}</span> : null}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-foreground">{title}</span>
        {subtitle ? (
          <span className="block truncate text-[11px] text-muted-foreground">{subtitle}</span>
        ) : null}
      </span>
      {trailing}
    </button>
  );
}

function MenuHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="flex items-center gap-1.5 border-b border-border p-1.5">
      <button
        type="button"
        onClick={onBack}
        aria-label="Back"
        className="flex size-6 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
      </button>
      <span className="truncate text-xs font-medium text-foreground">{title}</span>
    </div>
  );
}

/**
 * Composer attach (`+`) menu (redesign v2 state 10): an upward white dropdown that opens with the
 * `+` button or Cmd/Ctrl + /. "Reference an element" drills into the project's entity types and
 * then a searchable list of that type, inserting an @-mention on select. "Attach files" opens the
 * native file picker (upload wiring is stubbed until a message-attachment backend exists).
 */
export function AttachMenu({
  editor,
  projectId,
  disabled = false,
  accept,
  onFilesSelected,
}: AttachMenuProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<MenuView>("root");
  const [rootQuery, setRootQuery] = useState("");
  const [selectedType, setSelectedType] = useState<SelectedType | null>(null);
  const [entityQuery, setEntityQuery] = useState("");
  const [fileInput, setFileInput] = useState<HTMLInputElement | null>(null);

  const types = useQuery(api.mentions.listMentionableTypes, projectId ? { projectId } : "skip");
  const entities = useQuery(
    api.mentions.search,
    projectId && selectedType
      ? { projectId, query: entityQuery, types: [selectedType.key], limit: 20 }
      : "skip"
  );

  // Reset to the root view whenever the menu closes.
  useEffect(() => {
    if (!open) {
      setView("root");
      setRootQuery("");
      setEntityQuery("");
      setSelectedType(null);
    }
  }, [open]);

  // Cmd/Ctrl + / opens the menu while the editor is focused (design state 10).
  useEffect(() => {
    const dom = editor?.view.dom;
    if (!dom) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "/") {
        event.preventDefault();
        setOpen(true);
      }
    };
    dom.addEventListener("keydown", handleKeyDown);
    return () => dom.removeEventListener("keydown", handleKeyDown);
  }, [editor]);

  const insertMention = (item: { id: string; name: string; type: string }) => {
    editor
      ?.chain()
      .focus()
      .insertContent([
        { type: "mention", attrs: { id: item.id, label: item.name, mentionType: item.type } },
        { type: "text", text: " " },
      ])
      .run();
    setOpen(false);
  };

  const openFilePicker = () => {
    setOpen(false);
    // Defer so the popover unmount doesn't swallow the gesture that opens the OS dialog.
    window.setTimeout(() => fileInput?.click(), 0);
  };

  const handleFilesSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length === 0) {
      return;
    }
    if (onFilesSelected) {
      void onFilesSelected(files);
    } else {
      toast.info("File attachments aren’t available yet.");
    }
  };

  const normalizedRootQuery = rootQuery.trim().toLowerCase();
  const rootItems = [
    {
      key: "element",
      title: "Reference an element",
      subtitle: "Insert a mention",
      icon: <AtSign className="size-4" />,
      disabled: !projectId,
      onClick: () => setView("types"),
      trailing: <ChevronRight className="size-3.5 text-muted-foreground" />,
    },
    {
      key: "files",
      title: "Attach files",
      subtitle: "Upload from your device",
      icon: <FileUp className="size-4" />,
      onClick: openFilePicker,
    },
  ].filter((item) =>
    normalizedRootQuery ? item.title.toLowerCase().includes(normalizedRootQuery) : true
  );

  return (
    <>
      <input
        ref={setFileInput}
        data-testid="composer-attachment-input"
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={handleFilesSelected}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          aria-label="Add context"
          disabled={disabled || !editor}
          className={cn(
            buttonVariants({ variant: "ghost", size: "icon-sm" }),
            "size-7 shrink-0 p-0 text-muted-foreground"
          )}
        >
          <Plus className="size-[18px]" />
        </PopoverTrigger>
        <PopoverContent side="top" align="start" sideOffset={8} className="w-64 gap-0 p-0">
          {view === "root" ? (
            <div className="flex flex-col">
              <div className="border-b border-border p-2">
                <Input
                  value={rootQuery}
                  onChange={(event) => setRootQuery(event.target.value)}
                  placeholder="Search…"
                  autoFocus
                  className="h-8"
                  aria-label="Filter attach options"
                />
              </div>
              <div className="p-1">
                {rootItems.length === 0 ? (
                  <p className="px-2 py-3 text-center text-xs text-muted-foreground">No matches</p>
                ) : (
                  rootItems.map(({ key, ...item }) => <MenuRow key={key} {...item} />)
                )}
              </div>
            </div>
          ) : view === "types" ? (
            <div className="flex flex-col">
              <MenuHeader title="Reference an element" onBack={() => setView("root")} />
              <div className="max-h-64 overflow-y-auto p-1">
                <AsyncBoundary
                  data={types}
                  loading={<MenuNote>Loading…</MenuNote>}
                  empty={<MenuNote>No element types</MenuNote>}
                >
                  {(types) =>
                    types.map((type) => (
                      <MenuRow
                        key={type.key}
                        title={type.label}
                        trailing={<ChevronRight className="size-3.5 text-muted-foreground" />}
                        onClick={() => {
                          setSelectedType({ key: type.key, label: type.label });
                          setEntityQuery("");
                          setView("entities");
                        }}
                      />
                    ))
                  }
                </AsyncBoundary>
              </div>
            </div>
          ) : (
            <div className="flex flex-col">
              <MenuHeader
                title={selectedType?.label ?? "Elements"}
                onBack={() => setView("types")}
              />
              <div className="border-b border-border p-2">
                <Input
                  value={entityQuery}
                  onChange={(event) => setEntityQuery(event.target.value)}
                  placeholder={`Search ${selectedType?.label ?? "elements"}…`}
                  autoFocus
                  className="h-8"
                  aria-label="Search elements"
                />
              </div>
              <div className="max-h-64 overflow-y-auto p-1">
                <AsyncBoundary
                  data={entities}
                  loading={<MenuNote>Loading…</MenuNote>}
                  empty={<MenuNote>No matches</MenuNote>}
                >
                  {(entities) =>
                    entities.map((entity) => (
                      <MenuRow
                        key={entity.id}
                        title={entity.name}
                        subtitle={entity.subtitle}
                        onClick={() => insertMention(entity)}
                      />
                    ))
                  }
                </AsyncBoundary>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}
